/**
 * Procurement Operational Signals — Phase 1B
 * ─────────────────────────────────────────────────────────────────────
 * Single canonical cross-domain interpretation layer for procurement
 * health on a job. Procurement equivalent of
 * `resolvePermitOperationalSignals()`.
 *
 * Invariants (LOCKED):
 *   1. Pure derivation. Never mutates inputs, never persists, never
 *      emits queue events, never schedules notifications.
 *   2. Receives data explicitly — does NOT import the global app data
 *      context or any React/context module.
 *   3. NO automation imports, NO writers, NO scheduling logic.
 *   4. Reuses Phase 0A truth fields:
 *        - PurchaseOrder.etaConfirmed / vendorAckAt
 *        - ReceivingRecord.allocationBlocked / allocationBlockedReason
 *   5. Deterministic precedence order — see SIGNAL PRIORITY below.
 */

import type { Job, MaterialLineItem } from "@/data/types";
import type { PurchaseOrder } from "@/data/VendorPOContext";
import type { OrderingLineItem } from "@/data/orderingPacket/types";
import type {
  ReceivingRecord,
  JobAllocation,
} from "@/data/WarehouseInventoryContext";
import type { JobBomReconciliation } from "@/data/materialsReconciliation";

export type ProcurementSeverity = "neutral" | "info" | "warn" | "blocker";

export type ProcurementState =
  | "not_required"
  | "not_started"
  | "ordered"
  | "eta_unconfirmed"
  | "partially_received"
  | "allocation_blocked"
  | "materials_ready"
  | "substitution_risk"
  | "vendor_delay_risk"
  | "reconciliation_drift";

export type EtaConfidence =
  | "unknown"
  | "operator_estimate"
  | "vendor_confirmed";

export interface ProcurementSignalsInput {
  job: Job | null | undefined;
  purchaseOrders?: PurchaseOrder[];
  orderingPackets?: OrderingLineItem[];
  receivingRecords?: ReceivingRecord[];
  jobAllocations?: JobAllocation[];
  materialReconciliation?: JobBomReconciliation | null;
}

export interface ProcurementOperationalSignals {
  procurementState: ProcurementState;
  severity: ProcurementSeverity;

  installBlocker: boolean;
  installBlockerReason?: string;

  etaConfidence: EtaConfidence;
  etaConfirmed: boolean;
  vendorAckAt?: string;

  allocationBlocked: boolean;
  allocationBlockedReason?: string;

  substitutionRisk: boolean;
  substitutionReason?: string;

  reconciliationDrift: boolean;
  reconciliationSummary?: string;

  materialAlignmentOk: boolean;
  partialReceipt: boolean;

  missingCriticalItems: string[];

  summaryLabel: string;
  schedulingLabel: string;
  readinessLabel: string;
  installOpsLabel: string;
}

/* ── Heuristics ──────────────────────────────────────────────── */

const CRITICAL_CATEGORIES = new Set([
  "wall_panel", "wall panel", "panels",
  "door", "doors", "shower_door", "glass",
  "tub", "base", "shower_base", "pan",
  "fixture_primary",
]);

const CRITICAL_NAME_HINTS = [
  "panel", "base", "door", "glass", "tub", "pan", "valve", "diverter",
];

function isCriticalName(name: string): boolean {
  const n = (name || "").toLowerCase();
  return CRITICAL_NAME_HINTS.some((h) => n.includes(h));
}

function isCriticalLine(
  sku: string,
  productName: string,
  packets: OrderingLineItem[] | undefined,
): boolean {
  if (packets && packets.length) {
    const match = packets.find(
      (p) => p.actualSku === sku || p.projectedSku === sku,
    );
    if (match?.category && CRITICAL_CATEGORIES.has(match.category.toLowerCase())) {
      return true;
    }
  }
  return isCriticalName(productName);
}

/* ── Resolver ───────────────────────────────────────────────── */

const SEVERITY_RANK: Record<ProcurementSeverity, number> = {
  neutral: 0,
  info: 1,
  warn: 2,
  blocker: 3,
};

function maxSeverity(a: ProcurementSeverity, b: ProcurementSeverity): ProcurementSeverity {
  return SEVERITY_RANK[a] >= SEVERITY_RANK[b] ? a : b;
}

const NOT_REQUIRED: ProcurementOperationalSignals = {
  procurementState: "not_required",
  severity: "neutral",
  installBlocker: false,
  etaConfidence: "unknown",
  etaConfirmed: false,
  allocationBlocked: false,
  substitutionRisk: false,
  reconciliationDrift: false,
  materialAlignmentOk: true,
  partialReceipt: false,
  missingCriticalItems: [],
  summaryLabel: "Materials not required",
  schedulingLabel: "Materials not required",
  readinessLabel: "Materials not required",
  installOpsLabel: "Materials not required",
};

function hasMaterialFootprint(
  materialsList: MaterialLineItem[] | undefined,
  pos: PurchaseOrder[],
  packets: OrderingLineItem[] | undefined,
  reconciliation?: JobBomReconciliation | null,
): boolean {
  if (materialsList && materialsList.length > 0) return true;
  if (pos.length > 0) return true;
  if (packets && packets.length > 0) return true;
  if (reconciliation && reconciliation.rows.length > 0) return true;
  return false;
}

export function resolveProcurementSignals(
  input: ProcurementSignalsInput,
): ProcurementOperationalSignals {
  const { job } = input;
  const pos = input.purchaseOrders ?? [];
  const packets = input.orderingPackets ?? [];
  const receiving = input.receivingRecords ?? [];
  const allocations = input.jobAllocations ?? [];
  const reconciliation = input.materialReconciliation ?? null;
  const materialsList = job?.materialsList;

  if (!job) return NOT_REQUIRED;
  if (!hasMaterialFootprint(materialsList, pos, packets, reconciliation)) {
    return NOT_REQUIRED;
  }

  // ── ETA truth ────────────────────────────────────────────────
  const ackedPO = pos.find((p) => p.etaConfirmed && p.vendorAckAt);
  const anyEtaConfirmed = pos.some((p) => p.etaConfirmed === true);
  const allEtaConfirmed = pos.length > 0 && pos.every((p) => p.etaConfirmed === true);
  const etaConfidence: EtaConfidence = allEtaConfirmed
    ? "vendor_confirmed"
    : anyEtaConfirmed
      ? "operator_estimate"
      : pos.length > 0
        ? "operator_estimate"
        : "unknown";

  // ── Receiving aggregates ─────────────────────────────────────
  const blockedReceipts = receiving.filter((r) => r.allocationBlocked);
  const allocationBlocked = blockedReceipts.length > 0;
  const allocationBlockedReason = blockedReceipts[0]?.allocationBlockedReason;

  const fullyReceived = receiving.filter((r) => r.status === "Fully Received").length;
  const partial = receiving.some((r) => r.status === "Partially Received") ||
    (receiving.length > 0 && fullyReceived > 0 && fullyReceived < receiving.length);
  const allReceived = receiving.length > 0 && fullyReceived === receiving.length && !allocationBlocked;

  // ── Reconciliation drift ─────────────────────────────────────
  const reconciliationDrift = !!reconciliation && reconciliation.hasDrift;
  const reconciliationSummary = reconciliation
    ? reconciliation.counts.offSpec > 0
      ? `${reconciliation.counts.offSpec} off-spec line${reconciliation.counts.offSpec === 1 ? "" : "s"}`
      : reconciliation.counts.qtyDrift > 0
        ? `${reconciliation.counts.qtyDrift} qty drift`
        : undefined
    : undefined;
  const materialAlignmentOk = !reconciliationDrift;

  // ── Substitution risk ────────────────────────────────────────
  const substitutedLines = packets.filter((p) => p.substituted || p.vendorChanged);
  const substitutionRisk = substitutedLines.length > 0;
  const substitutionReason = substitutedLines.length
    ? substitutedLines[0].substitutionReason ||
      substitutedLines[0].vendorChangeReason ||
      `${substitutedLines.length} line${substitutedLines.length === 1 ? "" : "s"} substituted`
    : undefined;

  // ── Missing critical items ───────────────────────────────────
  // Critical lines on POs that have NOT been fully received OR are blocked.
  const missingCriticalItems: string[] = [];
  for (const po of pos) {
    for (const item of po.items) {
      if (!isCriticalLine(item.sku, item.productName, packets)) continue;
      const recForItem = receiving.filter(
        (r) => r.poId === po.poId && r.sku === item.sku,
      );
      const fullyOk =
        recForItem.length > 0 &&
        recForItem.every((r) => r.status === "Fully Received" && !r.allocationBlocked);
      if (!fullyOk) {
        missingCriticalItems.push(item.productName);
      }
    }
  }
  // Reconciliation: unallocated critical spec lines.
  if (reconciliation) {
    for (const row of reconciliation.rows) {
      if (row.status === "unallocated" && isCriticalLine(row.sku, row.itemName, packets)) {
        if (!missingCriticalItems.includes(row.itemName)) {
          missingCriticalItems.push(row.itemName);
        }
      }
    }
  }
  const hasMissingCritical = missingCriticalItems.length > 0;

  // ── State precedence ─────────────────────────────────────────
  let state: ProcurementState;
  let severity: ProcurementSeverity;
  let installBlocker = false;
  let installBlockerReason: string | undefined;
  let summary: string;

  if (allocationBlocked) {
    state = "allocation_blocked";
    severity = "blocker";
    installBlocker = true;
    installBlockerReason = allocationBlockedReason || "Allocation blocked due to receiving discrepancy";
    summary = `Allocation Blocked · ${allocationBlockedReason || "Discrepancy on receipt"}`;
  } else if (reconciliationDrift && reconciliation && reconciliation.counts.offSpec > 0) {
    state = "reconciliation_drift";
    severity = "blocker";
    installBlocker = true;
    installBlockerReason = `Material spec drift · ${reconciliationSummary}`;
    summary = "Material Spec Drift";
  } else if (reconciliationDrift) {
    state = "reconciliation_drift";
    severity = "warn";
    summary = `Material reconciliation drift · ${reconciliationSummary}`;
  } else if (hasMissingCritical) {
    state = "not_started";
    severity = "blocker";
    installBlocker = true;
    installBlockerReason = `Critical materials missing · ${missingCriticalItems[0]}`;
    summary = `Critical Materials Missing · ${missingCriticalItems.length}`;
  } else if (substitutionRisk) {
    state = "substitution_risk";
    severity = "warn";
    summary = `Substitution Pending Review · ${substitutionReason}`;
  } else if (pos.length > 0 && !anyEtaConfirmed) {
    state = "eta_unconfirmed";
    severity = "warn";
    summary = "ETA Awaiting Vendor";
  } else if (partial) {
    state = "partially_received";
    severity = "warn";
    summary = "Partial Materials Received";
  } else if (allReceived) {
    state = "materials_ready";
    severity = "info";
    summary = "Materials Ready";
  } else if (pos.length > 0) {
    state = "ordered";
    severity = anyEtaConfirmed ? "info" : "warn";
    summary = anyEtaConfirmed ? "Materials Ordered · ETA confirmed" : "Materials Ordered";
  } else {
    state = "not_started";
    severity = "warn";
    summary = "Procurement Not Started";
  }

  // ── Lens labels ─────────────────────────────────────────────
  const schedulingLabel =
    state === "allocation_blocked"
      ? "Material Hold · Allocation blocked"
      : state === "reconciliation_drift" && severity === "blocker"
        ? "Material Hold · Spec drift"
        : hasMissingCritical
          ? "Material Hold · Critical items missing"
          : state === "eta_unconfirmed"
            ? "ETA Unconfirmed"
            : state === "partially_received"
              ? "Partial Receipt"
              : state === "substitution_risk"
                ? "Substitution Review"
                : state === "materials_ready"
                  ? "Materials Cleared"
                  : summary;

  const readinessLabel =
    installBlocker
      ? `Material Blocker · ${installBlockerReason}`
      : state === "materials_ready"
        ? "Materials Ready"
        : state === "eta_unconfirmed"
          ? "ETA Awaiting Vendor"
          : state === "partially_received"
            ? "Partial Receipt"
            : summary;

  const installOpsLabel =
    installBlocker
      ? `Install Blocker · ${installBlockerReason}`
      : severity === "warn"
        ? summary
        : state === "materials_ready"
          ? "Materials Ready"
          : summary;

  // Final overall severity = max of computed + alignment status
  severity = maxSeverity(severity, materialAlignmentOk ? "neutral" : "warn");

  return {
    procurementState: state,
    severity,
    installBlocker,
    installBlockerReason,
    etaConfidence,
    etaConfirmed: allEtaConfirmed,
    vendorAckAt: ackedPO?.vendorAckAt,
    allocationBlocked,
    allocationBlockedReason,
    substitutionRisk,
    substitutionReason,
    reconciliationDrift,
    reconciliationSummary,
    materialAlignmentOk,
    partialReceipt: partial,
    missingCriticalItems,
    summaryLabel: summary,
    schedulingLabel,
    readinessLabel,
    installOpsLabel,
  };
}
